<script>
    import { onMount } from "svelte";

    import {
        getDate,
        getDaysDifferFrom,
        getUrlWithoutSearch,
        goToURL,
        showError,
    } from "../../../Utils/utilFunctions.svelte";

    import { user } from "../../../stores";

    import {
        CardLayout,
        SearchDateRangeInput,
        SearchInput,
        SearchSelect,
    } from "../../../Components/Common/index.svelte";

    import { apiMethods } from "../../../Services/index.svelte";

    import DashboardContainer from "../components/DashboardContainer.svelte";
    import DoctorGeneralInfoChart from "../components/DoctorGeneralInfoChart.svelte";
    import DoctorSurgeonInfoChart from "../components/DoctorSurgeonInfoChart.svelte";
    import DoctorEncounterInfoChart from "../components/DoctorEncounterInfoChart.svelte";
    import DoctorPendingPaymentsInfoChart from "../components/DoctorPendingPaymentsInfoChart.svelte";

    let filters = { dateFrom: null, dateTo: null, interval: 1 }; // default interval this fortnight

    let isGettingData = false,
        dashboardData = {};

    const filterIntervals = [
        { id: 1, type: "This fortnight" },
        { id: 2, type: "Last fortnight" },
        { id: 3, type: "Last 30 days" },
        { id: 4, type: "Last 365 days" },
        { id: 5, type: "This month" },
        { id: 6, type: "This financial year" },
        { id: 7, type: "All time" },
        { id: 8, type: "Custom" },
    ];

    onMount(async () => {
        // this will load the data
        handleIntervalSelect();

        const params = window.location.href.split("?")[1];

        if (params) {
            const paramsArr = params.split("&");

            if (paramsArr.some((para) => para.includes("go="))) {
                const redirectURL = `/${paramsArr
                    .filter((para) => !para.includes("sso="))
                    .join("&")
                    .replace("go=", "")}`;

                goToURL(redirectURL);
            } else {
                getUrlWithoutSearch();
                goToURL("/");
            }
        }
    });

    const loadDashboardData = async (params) => {
        isGettingData = true;

        await apiMethods.getMethod
            .getDashboardDataForDoctor(params)
            .then((res) => _handleDashboardData(res))
            .catch((err) => showError(err));

        isGettingData = false;
    };

    const handleIntervalSelect = () => {
        switch (filters.interval) {
            case 1:
                // include this week and next week
                _getFortnightDateRange();
                break;
            case 2:
                // include last week and the week before
                _getLastFortnightDateRange();
                break;
            case 3:
                // yesterday to 30 days prior
                _getLastMonthDateRange();
                break;
            case 4:
                // yesterday to 365 days prior
                _getLastYearDateRange();
                break;
            case 5:
                _getThisMonthDateRange();
                break;
            case 6:
                _getThisFinancialYearDateRange();
                break;
            case 7:
                delete filters.dateFrom;
                delete filters.dateTo;
                break;
            case 8:
            default:
                break;
        }

        if (filters.dateFrom && filters.dateTo)
            loadDashboardData(_getSearchParams());
    };

    const handleDateSelect = () => {
        if (filters.dateFrom && filters.dateTo)
            loadDashboardData(_getSearchParams());
    };

    const redirectToMyInvoices = () => goToURL(`/MyInvoices`);

    // HELPER FUNCTIONS
    const _handleDashboardData = (res) => {
        if (!res || res.error)
            showError(
                res.error ??
                    "Failed to load dashboard data, please try again later or contact support...",
            );
        else {
            // const tempData = {
            //     surgeonName: "string",
            //     totalPayments: 12345,
            //     hours: 10,
            //     minutes: 20,
            // };

            // const tempData2 = {
            //     surgeonName: "satring",
            //     totalPayments: 3322,
            //     hours: 1,
            //     minutes: 2,
            // };

            // const tempData3 = {
            //     surgeonName: "sbtring",
            //     totalPayments: 5555,
            //     hours: 1,
            //     minutes: 2,
            // };

            // res.surgeonPayments = [
            //     ...res.surgeonPayments,
            //     tempData,
            //     tempData2,
            //     tempData3,
            // ];

            // res.totalEncoutnersByInsurance = {
            //     additionalProp1: 1,
            //     additionalProp2: 2,
            //     additionalProp3: 3,
            //     additionalProp4: 4,
            //     additionalProp5: 5,
            //     additionalProp6: 6,
            //     additionalProp7: 7,
            //     additionalProp8: 8,
            //     additionalProp9: 9,
            //     additionalProp10: 10,
            //     additionalProp11: 11,
            //     additionalProp12: 12,
            // };

            // res.allPendingPaymentsOverview = [
            //     {
            //         daysOld: "< 30 days",
            //         totalPendingPayments: 10,
            //     },
            //     {
            //         daysOld: "30-60 days",
            //         totalPendingPayments: 100,
            //     },
            //     {
            //         daysOld: "> 60 days",
            //         totalPendingPayments: 1000,
            //     },
            // ];

            dashboardData = { ...res };
        }
    };

    const _getSearchParams = () => {
        let params = {};

        if (filters.dateFrom) params["filter[startdate]"] = filters.dateFrom;

        if (filters.dateTo) params["filter[enddate]"] = filters.dateTo;

        return params;
    };

    const _getFortnightDateRange = () => {
        const today = new Date();

        filters.dateFrom = getDate(getDaysDifferFrom(1 - today.getDay()));
        filters.dateTo = getDate(getDaysDifferFrom(14 - today.getDay()));
    };

    const _getLastFortnightDateRange = () => {
        const today = new Date();

        const firstDayOfThisFortnight = getDate(
            getDaysDifferFrom(1 - today.getDay()),
        );

        filters.dateFrom = getDate(
            getDaysDifferFrom(-14, firstDayOfThisFortnight),
        );
        filters.dateTo = getDate(
            getDaysDifferFrom(-1, firstDayOfThisFortnight),
        );
    };

    const _getLastMonthDateRange = () => {
        const today = new Date();

        const dateTo = new Date(today);
        dateTo.setDate(today.getDate() - 1);

        const dateFrom = new Date(dateTo);
        dateFrom.setDate(dateTo.getDate() - 30);

        filters.dateFrom = getDate(dateFrom, "-", true);
        filters.dateTo = getDate(dateTo, "-", true);
    };

    const _getLastYearDateRange = () => {
        const today = new Date();

        const dateTo = new Date(today);
        dateTo.setDate(today.getDate() - 1);

        const dateFrom = new Date(dateTo);
        dateFrom.setDate(dateTo.getDate() - 365);

        filters.dateFrom = getDate(dateFrom, "-", true);
        filters.dateTo = getDate(dateTo, "-", true);
    };

    const _getThisMonthDateRange = () => {
        const today = new Date();

        const dateFrom = new Date(today);
        dateFrom.setDate(1);

        const dateTo = new Date(today);
        const daysInMonth = new Date(
            today.getFullYear(),
            today.getMonth() + 1,
            0,
        ).getDate();
        dateTo.setDate(daysInMonth);

        filters.dateFrom = getDate(dateFrom, "-", true);
        filters.dateTo = getDate(dateTo, "-", true);
    };

    const _getThisFinancialYearDateRange = () => {
        const today = new Date();
        const thisYear = today.getFullYear();

        const dateFrom = new Date(thisYear - 1, 6, 1);
        const dateTo = new Date(thisYear, 5, 30);

        filters.dateFrom = getDate(dateFrom, "-", true);
        filters.dateTo = getDate(dateTo, "-", true);
    };
</script>

<div class="flex flex-col justify-between items-center lg:flex-row">
    <div class="font-bold text-2xl p-5">
        Welcome {$user.name}
    </div>

    <div class="flex flex-col mr-2 mb-2 gap-5 lg:flex-row">
        <SearchSelect
            bind:value={filters.interval}
            title="Interval"
            onChangeFunc={handleIntervalSelect}
            isDisabled={isGettingData}
        >
            {#each filterIntervals as interval}
                <option value={interval.id}>{interval.type}</option>
            {/each}
        </SearchSelect>

        {#if !$user.settings?.disableDateRangeInput}
            <SearchDateRangeInput
                bind:startDate={filters.dateFrom}
                bind:endDate={filters.dateTo}
                title="Date Range"
                inputClass={"borderB w-[272px]"}
                on:change={handleDateSelect}
                showClearButton
                disabled={isGettingData || filters.interval != 8}
            />
        {:else}
            <SearchInput
                bind:value={filters.dateFrom}
                title="Date from"
                type="date"
                isLong
                inputClass="borderB"
                inputFunc={handleDateSelect}
                isDisabled={isGettingData || filters.interval != 8}
                maxDate={filters.dateTo ?? undefined}
            />

            <SearchInput
                bind:value={filters.dateTo}
                title="Date to"
                type="date"
                isLong
                inputClass="borderB"
                inputFunc={handleDateSelect}
                isDisabled={isGettingData || filters.interval != 8}
                minDate={filters.dateFrom ?? undefined}
            />
        {/if}
    </div>
</div>

<div class="flex flex-1 flex-col">
    <div class="flex flex-1 flex-col mx-4 mb-4">
        <div class="relative flex-1">
            <div
                class="flex flex-col overflow-y-auto absolute inset-0 gap-4 mt-4"
            >
                <DashboardContainer
                    title={"General"}
                    isLoadingData={isGettingData}
                >
                    <DoctorGeneralInfoChart {dashboardData} {filters} />
                </DashboardContainer>

                <DashboardContainer
                    title={"Surgeons"}
                    isLoadingData={isGettingData}
                >
                    <DoctorSurgeonInfoChart {dashboardData} />
                </DashboardContainer>

                <DashboardContainer
                    title="Encounters Detail"
                    isLoadingData={isGettingData}
                >
                    <DoctorEncounterInfoChart {dashboardData} {filters} />
                </DashboardContainer>

                <DashboardContainer
                    title="Pending Payments Detail"
                    isLoadingData={isGettingData}
                >
                    <DoctorPendingPaymentsInfoChart {dashboardData} {filters} />
                </DashboardContainer>
            </div>
        </div>
    </div>
</div>

<!-- <div class="flex w-full items-center justify-center px-4">
    <button
        class="btn btn-secondary w-1/2 text-md text-white font-bold"
        on:click|preventDefault={redirectToMyInvoices}
    >
        Download a report
    </button>
</div> -->
